import React from 'react';
import { Box, Typography, IconButton, TextField, useTheme, Paper } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Select } from '@shared/components/Select/Select';
import { IDropdownOptions } from '@shared/interface';
import { FormField } from '@shared/components/FormField/FormField';

interface PremiumIdEntry {
  premiumId: number;
  amount: number;
}

interface PremiumIdInputProps {
  value: PremiumIdEntry[];
  onChange: (value: PremiumIdEntry[]) => void;
  premiumOptions: IDropdownOptions[];
  error?: string;
  required?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
}

export const PremiumIdInput: React.FC<PremiumIdInputProps> = ({
  value,
  onChange,
  premiumOptions,
  error,
  required = false,
  disabled = false,
  'data-testid': testId,
}) => {
  const theme = useTheme();

  const handleAddEntry = () => {
    onChange([...value, { premiumId: null, amount: 0 }]);
  };

  const handleRemoveEntry = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handlePremiumIdChange = (index: number, premiumId: number) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], premiumId };
    onChange(newValue);
  };

  const handleAmountChange = (index: number, amount: number) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], amount };
    onChange(newValue);
  };

  return (
    <Box data-testid={testId}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {/* <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.primary,
            fontWeight: 500,
          }}
        > */}
        <FormField
          label="Premium IDs"
          required={required}
          tooltip="For Premium Currency: Enter 'a24=32000' to give 32000 GM. For Premium Accounts: Set duration above, then use 'a178=1' for s1914."
        />
        {/* </Typography> */}
        <IconButton
          onClick={handleAddEntry}
          disabled={disabled}
          size="small"
          sx={{
            ml: 1,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </Box>

      {value.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: theme.palette.background.default,
            border: `1px dashed ${theme.palette.divider}`,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No premium IDs added. Click the + button to add one.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {value.map((entry, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Select
                  label="Premium"
                  value={entry.premiumId}
                  onChange={(premiumId) => handlePremiumIdChange(index, premiumId as number)}
                  options={premiumOptions}
                  disabled={disabled}
                  useOptionId
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <TextField
                  label="Amount"
                  type="number"
                  value={entry.amount || ''}
                  onChange={(e) => handleAmountChange(index, parseInt(e.target.value) || 0)}
                  fullWidth
                  disabled={disabled}
                  inputProps={{ min: 0 }}
                />
              </Box>
              <IconButton
                onClick={() => handleRemoveEntry(index)}
                disabled={disabled}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.lighter,
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {error && (
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: 'block',
            color: theme.palette.error.main,
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};
